import * as React from 'react';
import { Alert } from 'reactstrap';
import { LProps, withLocalization } from '../utils/wrappers';
import { State } from '../redux/state';
import * as selectors from '../redux/selectors';
import { CompetitionPledge } from '../userApi';
import {
  isUserTypeEligibleForPledge, getScoreLevelPhoto,
  CompetitionPledges, IntroduceYourselfPledgeUUID, isCloseOutSurveyPledge
} from '../utils/userApiHelper';
import { orJoin } from '../utils/strings';
import logger from '../utils/logger';
import { compose } from 'redux';
import { connect } from 'react-redux';

interface ManualProps {
  pledge: CompetitionPledge;
}

interface Props extends ManualProps {
  message: string;
}

interface UserIneligibleForPledgeState {
  hasError: boolean;
}

// IMPORTANT: This does not handle release date eligibility.
class UserIneligibleForPledge extends React.Component<LProps<Props>, UserIneligibleForPledgeState> {
  public state = { hasError: false };

  public static getDerivedStateFromError(_error: Error) {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  public componentDidCatch(error: Error, info: React.ErrorInfo) {
    // You can also log the error to an error reporting service
    logger.error(error, info);
  }

  public render() {
    const { hasError } = this.state;
    if (hasError) return <div />;
    const { message } = this.props;
    return (
      <Alert
        className="cc__user_ineligible_for_pledge"
        color="secondary"
      >
        <h6
          dangerouslySetInnerHTML={{
            __html: message
          }}
        />
      </Alert>
    );
  }
}

const mapStateToProps = (state: State, ownProps: LProps<Props>): Partial<Props> => {
  const { user } = state;
  const { pledge, t, i18n, formatNumber } = ownProps;

  if (!selectors.isIntroduceYourselfPledgeComplete(state)
    && !selectors.hasCompletedPostOnboardingAction(state)
    && pledge.uuid !== IntroduceYourselfPledgeUUID
    && !isCloseOutSurveyPledge(pledge)) {
    return {
      message: t('Introduce yourself to unlock actions and keep going.')
    };
  }

  const typeIneligible = !isUserTypeEligibleForPledge(user, ownProps.pledge);
  if (typeIneligible) {
    return {
      message: t('You must be a {{userType}} user to take this action.', { userType: orJoin(pledge.userTypes.map((ut) => ut.toString()), i18n)})
    };
  }
  const { competitionScoreLevels, competitionUserScores } = state;
  const score = competitionUserScores[user.id];
  const userPoints = score ? score.points : 0;
  const requiredLevel = (competitionScoreLevels || []).find(
    (sl) => sl.uuid === pledge.scoreLevelUUID
  ) || selectors.nextUserScoreLevel(state);
  const isScoreIneligible = Boolean(
    pledge.scoreLevelUUID &&
    requiredLevel &&
    !selectors.hasReachedScoreLevel(state, requiredLevel, userPoints)
  );
  if (isScoreIneligible) {
    if (!requiredLevel) {
      return {
        message: t('You are ineligible for this pledge')
      };
    }
    const pointsToRequiredLevel = Math.max(0, (requiredLevel.threshold || 0) - userPoints);
    const icon = getScoreLevelPhoto(requiredLevel)
      ? `<img src="${getScoreLevelPhoto(requiredLevel)}" alt="${requiredLevel.name}" />`
      : undefined;
    const message = t('{{pointsToNextLevel}} more points to unlock, {{total}} total — achieve {{icon}} {{scoreLevel}} to take this action!', {
      pointsToNextLevel: formatNumber(pointsToRequiredLevel, 0),
      total: formatNumber(requiredLevel.threshold || 0, 0),
      scoreLevel: requiredLevel.name,
      icon
    });
    return {
      message
    };
  }
  return {
    message: t('You are ineligible for this pledge')
  };
};

export default compose(
  withLocalization('userIneligibleForPledge'),
  connect(mapStateToProps)
)(UserIneligibleForPledge) as React.SFC<ManualProps>;
